import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Save, Trash2, Pencil, Info } from "lucide-react";
import { toast } from "sonner";
import RoutingContextQuickAdd from "@/components/RoutingContextQuickAdd";

/**
 * Compact editor for the authoritative Routing Context of an Entity or Project.
 * `kind`: "entity" | "project"
 * `record`: the current entity/project doc (must include `routing_context`)
 * `api`:   { setRoutingContext(id, payload), clearRoutingContext(id) }
 * `canEdit`: bool — when false the block renders read-only.
 *
 * The field set is intentionally short and plain-language. Each field is
 * free text, capped at 4000 chars server-side. Storing several fields
 * (instead of one blob) lets Intake render them under labelled headings
 * so the LLM can match `relevant_signals` and honour `exclusions` cleanly.
 */
const FIELDS = [
  { key: "purpose", label: "Purpose", placeholder: "1–2 sentences on what this is for right now.", rows: 2 },
  { key: "scope", label: "Scope (project)", placeholder: "What's inside vs outside this project's scope. Skip for entity-level context.", rows: 3, projectOnly: true },
  { key: "relevant_signals", label: "Relevant signals", placeholder: "Bullets: sources that clearly belong here. Be specific.", rows: 4 },
  { key: "exclusions", label: "Exclusions / out-of-scope", placeholder: "Bullets: what does NOT belong here even if it looks close.", rows: 3 },
  { key: "current_priorities", label: "Current priorities / objective", placeholder: "Where the focus is now.", rows: 3 },
  { key: "interpretation_rules", label: "Interpretation rules", placeholder: "Important gotchas HQ should honour when routing.", rows: 3 },
];

export default function RoutingContextEditor({ kind, record, api, canEdit = true, onSaved }) {
  const rc = record?.routing_context || null;
  const [editing, setEditing] = useState(!rc);
  const [mode, setMode] = useState("quick");     // "quick" | "advanced"
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(() => Object.fromEntries(FIELDS.map(f => [f.key, rc?.[f.key] || ""])));

  useEffect(() => {
    // Re-sync when the underlying record changes (e.g. after save reload).
    setValues(Object.fromEntries(FIELDS.map(f => [f.key, rc?.[f.key] || ""])));
    setEditing(!rc);
    setMode("quick");
  }, [record?.id, rc?.version]);

  const fields = FIELDS.filter(f => !f.projectOnly || kind === "project");
  const hasAny = fields.some(f => (values[f.key] || "").trim());

  const save = async () => {
    if (!hasAny) return toast.error("Add at least one field before saving");
    setSaving(true);
    try {
      const payload = Object.fromEntries(fields.map(f => [f.key, values[f.key] || ""]));
      await api.setRoutingContext(record.id, payload);
      toast.success("Routing Context updated");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear Routing Context? Intake will fall back to the entity/project metadata until you set a new one.")) return;
    try {
      await api.clearRoutingContext(record.id);
      toast.success("Routing Context cleared");
      setEditing(true);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <section className="p-4 md:p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid={`routing-context-${kind}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-eyebrow flex items-center gap-1.5"><Sparkles className="w-3 h-3"/> Routing Context</div>
          <div className="text-sm text-white/70 mt-1">
            The authoritative definition HQ Intake reads first when routing.
            {" "}<span className="text-white/40">Overrides stale descriptions or older notes.</span>
          </div>
          {rc && (
            <div className="text-[10px] text-white/40 mt-1">
              v{rc.version} · updated {String(rc.updated_at || "").slice(0, 19)} UTC
            </div>
          )}
        </div>
        {canEdit && rc && !editing && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-white/70 hover:text-white hover:bg-white/10" data-testid={`routing-context-edit-${kind}`}>
              <Pencil className="w-3.5 h-3.5 mr-1.5"/> Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={clear} className="text-red-300 hover:text-red-200 hover:bg-red-500/10" data-testid={`routing-context-clear-${kind}`}>
              <Trash2 className="w-3.5 h-3.5"/>
            </Button>
          </div>
        )}
      </div>

      {!editing && rc ? (
        <div className="space-y-3 text-sm">
          {fields.map(f => {
            const v = (rc[f.key] || "").trim();
            if (!v) return null;
            return (
              <div key={f.key} data-testid={`routing-context-view-${f.key}`}>
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{f.label}</div>
                <div className="text-white/85 whitespace-pre-wrap leading-relaxed">{v}</div>
              </div>
            );
          })}
        </div>
      ) : canEdit ? (
        <div className="space-y-3">
          {/* Mode toggle: Quick Add (default) vs Advanced (existing form). */}
          <div className="inline-flex rounded-full border border-white/10 p-0.5 bg-white/[0.03] text-xs">
            <button type="button"
              onClick={() => setMode("quick")}
              className={`px-3 py-1 rounded-full transition ${mode === "quick" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid={`routing-context-mode-quick-${kind}`}>
              Quick Add
            </button>
            <button type="button"
              onClick={() => setMode("advanced")}
              className={`px-3 py-1 rounded-full transition ${mode === "advanced" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid={`routing-context-mode-advanced-${kind}`}>
              Advanced
            </button>
          </div>

          {mode === "quick" ? (
            <RoutingContextQuickAdd
              kind={kind} record={record} api={api}
              onSaved={() => { setEditing(false); onSaved?.(); }}
            />
          ) : (
            <>
              {fields.map(f => (
                <div key={f.key}>
                  <Label htmlFor={`rc-${kind}-${f.key}`} className="text-[10px] uppercase tracking-wider text-white/40">{f.label}</Label>
                  <Textarea id={`rc-${kind}-${f.key}`}
                    rows={f.rows} value={values[f.key] || ""}
                    onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="bg-white/5 border-white/10 text-sm mt-1"
                    data-testid={`routing-context-input-${f.key}`}/>
                </div>
              ))}
              <div className="flex items-center justify-end gap-2 pt-1">
                {rc && (
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setValues(Object.fromEntries(FIELDS.map(f => [f.key, rc[f.key] || ""]))); }}
                    className="text-white/60 hover:text-white hover:bg-white/10">Cancel</Button>
                )}
                <Button size="sm" onClick={save} disabled={saving || !hasAny}
                  className="rounded-full bg-white text-black hover:bg-white/90" data-testid={`routing-context-save-${kind}`}>
                  <Save className="w-3.5 h-3.5 mr-1.5"/> {saving ? "Saving…" : (rc ? "Update" : "Save context")}
                </Button>
              </div>
              <div className="flex items-start gap-1.5 text-[11px] text-white/40 pt-1">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0"/>
                <span>Intake will read this record first, then entity/project metadata, then other notes as secondary evidence. Every routed IntakeItem stores which version of this context shaped the decision.</span>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-xs text-white/40 italic">No authoritative Routing Context set. Ask an admin to configure one.</div>
      )}
    </section>
  );
}
